/* PB Cart Drawer · build Horizon/Pitch v2 · <script type="module"> */
let StandardEvents = null;
try { ({ StandardEvents } = await import('@shopify/events')); } catch (e) { /* tema senza @shopify/events */ }

const PB_TAG = 'cart-drawer';
const SRC = 'pb-cart-drawer';
const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
const CART_TRIGGER_SELECTORS = [
  '[data-drawer-open="cart-drawer"]',
  '[data-cart-toggle]',
  'a[href$="/cart"]',
  'a[href*="/cart?"]',
].join(',');

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.panel  = this.querySelector('[data-cd-panel]');
    this.recsEl = this.querySelector('[data-cd-recommendations]');
    this.sectionId = this.dataset.sectionId;
    this.urls = {
      change: this.dataset.cartChangeUrl,
      add:    this.dataset.cartAddUrl,
      recs:   this.dataset.recommendationsUrl,
    };
    this.lastOpener = null;
    this.lastAddedProductId = null;
    this.fetching = false;
    this._refreshing = null;
    this._refreshT = null;
    this._recsFor = null;
  }

  connectedCallback() {
    this.bindEvents();
    window.PBCartDrawer = this;
    document.addEventListener('pb:cart:open', () => this.open());
  }

  get isOpen() { return this.classList.contains('is-open'); }
  get count()  { return parseInt(this.dataset.cdCount, 10) || 0; }

  /* ─────────────── Eventi UI ─────────────── */
  bindEvents() {
    this.querySelectorAll('[data-cd-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.querySelector('[data-cd-overlay]')?.addEventListener('click', () => this.close());

    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'Tab') this.handleTab(e);
      if (e.key === 'Enter' && e.target.matches('[data-cd-discount-input]')) {
        e.preventDefault();
        this.applyDiscount(e.target.value.trim());
      }
    });

    this.panel.addEventListener('click', (e) => {
      const step     = e.target.closest('[data-cd-step]');
      const remove   = e.target.closest('[data-cd-remove]');
      const add      = e.target.closest('[data-cd-add]');
      const checkout = e.target.closest('[data-cd-checkout]');
      const prev     = e.target.closest('[data-cd-prev]');
      const next     = e.target.closest('[data-cd-next]');
      const dot      = e.target.closest('[data-cd-dot]');
      const dApply   = e.target.closest('[data-cd-discount-apply]');
      const dRemove  = e.target.closest('[data-cd-discount-remove]');
      const addon    = e.target.closest('[data-cd-addon]');
      if (step)     return this.onStep(step);
      if (remove)   return this.changeLine(remove.dataset.key, 0, remove.closest('[data-cd-item]'));
      if (add)      return this.onUpsellAdd(add);
      if (checkout) return this.onCheckout();
      if (prev)     return this.slideUpsell(-1);
      if (next)     return this.slideUpsell(1);
      if (dot)      return this.slideToUpsell(parseInt(dot.dataset.cdDot, 10));
      if (dApply)   { const i = this.querySelector('[data-cd-discount-input]'); return this.applyDiscount(i ? i.value.trim() : ''); }
      if (dRemove)  return this.removeDiscount();
      if (addon)    return this.onAddonToggle(addon);
    });

    this.panel.addEventListener('change', (e) => {
      const qty = e.target.closest('[data-cd-qty]');
      if (qty) return this.changeLine(qty.dataset.key, Math.max(0, parseInt(qty.value, 10) || 0), qty.closest('[data-cd-item]'));
      const variant = e.target.closest('[data-cd-variant]');
      if (variant) this.onVariantChange(variant);
    });

    document.addEventListener('variant:update', (e) => {
      const pid = e.detail?.data?.productId;
      if (pid) this.lastAddedProductId = pid;
    });
  }

  onStep(btn) {
    const item  = btn.closest('[data-cd-item]');
    const input = item.querySelector('[data-cd-qty]');
    const next  = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(btn.dataset.cdStep, 10));
    this.changeLine(btn.dataset.key, next, item);
  }

  /* ─────────────── Apertura / chiusura ─────────────── */
  open({ refresh = true } = {}) {
    if (this.isOpen) { if (refresh) this.scheduleRefresh(); return; }
    this.lastOpener = document.activeElement;
    this.hidden = false;
    this.lockScroll();
    // doppio rAF: il browser applica display prima della transizione
    requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
    if (refresh) this.refreshFromServer();
    this.fetchRecommendations();
    setTimeout(() => this.querySelector('[data-cd-close]')?.focus(), 320);
  }

  async openAfterRefresh() {
    await this.refreshFromServer();
    if (this.isOpen) this.fetchRecommendations();
    else this.open({ refresh: false });
  }

  close() {
    if (!this.isOpen) return;
    this.classList.remove('is-open');
    this.unlockScroll();
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      this.hidden = true;
      this.lastOpener?.focus?.();
    };
    this.panel.addEventListener('transitionend', finish, { once: true });
    setTimeout(finish, 500); // fallback (prefers-reduced-motion, tab in background)
  }

  lockScroll() {
    const sw = window.innerWidth - document.documentElement.clientWidth;
    document.documentElement.style.overflow = 'hidden';
    if (sw > 0) document.documentElement.style.paddingRight = `${sw}px`;
  }
  unlockScroll() {
    document.documentElement.style.overflow = '';
    document.documentElement.style.paddingRight = '';
  }

  handleTab(e) {
    const focusable = [...this.panel.querySelectorAll(
      'a[href],button:not([disabled]),input,textarea,[tabindex]:not([tabindex="-1"])'
    )].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  setLoading(state) { this.classList.toggle('is-loading', state); }

  /* ─────────────── HTTP helpers ─────────────── */
  sectionsParams() { return { sections: this.sectionId, sections_url: window.location.pathname }; }

  async post(url, body, withSections = true) {
    const res = await fetch(url, {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(withSections ? { ...body, ...this.sectionsParams() } : body),
    });
    const data = await res.json();
    if (data.status) throw new Error(data.description || data.message || 'Errore carrello');
    return data;
  }

  /* ─────────────── Sincronizzazione ─────────────── */
  refreshFromServer() {
    if (this._refreshing) return this._refreshing;
    this._refreshing = (async () => {
      try {
        const res  = await fetch(`${window.location.pathname}?sections=${this.sectionId}`);
        const data = await res.json();
        this.applySections(data);
        await this.removeOrphanedAddon();
      } catch (err) {
        console.error('[CartDrawer] refresh', err);
      } finally {
        this._refreshing = null;
      }
    })();
    return this._refreshing;
  }

  scheduleRefresh(delay = 150) {
    clearTimeout(this._refreshT);
    this._refreshT = setTimeout(() => this.refreshFromServer(), delay);
  }

  syncTheme(cartData = null) {
    this.updateCartCount(this.count);
    document.dispatchEvent(new CustomEvent('pb:cart:updated', {
      bubbles: true, detail: { source: SRC, itemCount: this.count, cart: cartData },
    }));
  }

  updateCartCount(n) {
    document.querySelectorAll('[data-cart-count], .cart-count, .header-actions__cart-count, .cart-link__bubble-num, .site-header__cart-count')
      .forEach((el) => { el.textContent = n; });
    document.querySelectorAll('[data-cart-count-bubble], .cart-link__bubble, .site-header__cart-indicator')
      .forEach((el) => {
        el.classList.toggle('cart-link__bubble--visible', n > 0);
        el.classList.toggle('hide', n === 0);
      });
  }

  /* ─────────────── Mutazioni ─────────────── */
  async changeLine(key, quantity, itemEl) {
    if (this.fetching) return;
    this.fetching = true;
    this.setLoading(true);
    if (quantity === 0 && itemEl) itemEl.classList.add('is-removing');
    try {
      const data = await this.post(this.urls.change, { id: key, quantity });
      this.applySections(data.sections);
      if (this.count === 0) { this.recsEl.innerHTML = ''; this._recsFor = null; }
      await this.removeOrphanedAddon();
      this.syncTheme(data);
    } catch (err) {
      console.error('[CartDrawer] change', err);
      itemEl?.classList.remove('is-removing');
    } finally {
      this.fetching = false;
      this.setLoading(false);
    }
  }

  /**
   * Se nel carrello restano solo addon e/o righe gratuite (gift Bogos), rimuove gli addon.
   */
  async removeOrphanedAddon() {
    if (this.count === 0) return;
    const addonKeys = [...this.querySelectorAll('[data-cd-addon][data-addon-key]')]
      .map((el) => el.dataset.addonKey).filter(Boolean);
    if (!addonKeys.length) return;

    const hasPaidProduct = [...this.querySelectorAll('[data-cd-item]')]
      .some((li) => !addonKeys.includes(li.dataset.key) && !li.hasAttribute('data-free'));
    if (hasPaidProduct) return;

    try {
      for (let i = 0; i < addonKeys.length; i++) {
        const isLast = i === addonKeys.length - 1;
        const data = await this.post(this.urls.change, { id: addonKeys[i], quantity: 0 }, isLast);
        if (isLast) this.applySections(data.sections);
      }
      this.syncTheme();
    } catch (err) {
      console.error('[CartDrawer] removeOrphanedAddon', err);
    }
  }

  async addItems(items) {
    const data = await this.post(this.urls.add, { items });
    this.applySections(data.sections);
    this.syncTheme(data);
    return data;
  }

  /** Solo con intercept=true (temi senza eventi cart). */
  async addFromForm(form, submitter) {
    if (form.dataset.pbBusy) return;
    form.dataset.pbBusy = '1';
    const fd  = new FormData(form, submitter);
    fd.append('sections', this.sectionId);
    fd.append('sections_url', window.location.pathname);
    const btn = submitter || form.querySelector('[type="submit"]');
    btn?.setAttribute('disabled', '');
    try {
      const res  = await fetch(this.urls.add, { method: 'POST', headers: { Accept: 'application/json' }, body: fd });
      const data = await res.json();
      if (data.status) throw new Error(data.description || 'Errore');
      this.lastAddedProductId = data.product_id || data.items?.[0]?.product_id || null;
      this.applySections(data.sections);
      this.syncTheme(data);
      this.open({ refresh: false });
    } catch (err) {
      console.error('[CartDrawer] add', err);
    } finally {
      btn?.removeAttribute('disabled');
      delete form.dataset.pbBusy;
    }
  }

  /* ─────────────── Addon ─────────────── */
  async onAddonToggle(addonEl) {
    if (addonEl.classList.contains('is-loading')) return;
    const variantId = parseInt(addonEl.dataset.addonVariantId, 10);
    const cartKey   = addonEl.dataset.addonKey;
    const isInCart  = !!cartKey;
    const existing  = [...this.querySelectorAll('[data-cd-addon][data-addon-key]')]
      .map((el) => el.dataset.addonKey).filter(Boolean);

    addonEl.classList.add('is-loading');
    addonEl.classList.toggle('is-checked', !isInCart);
    try {
      let data;
      if (isInCart) {
        data = await this.post(this.urls.change, { id: cartKey, quantity: 0 });
      } else {
        for (const key of existing) await this.post(this.urls.change, { id: key, quantity: 0 }, false);
        data = await this.post(this.urls.add, { items: [{ id: variantId, quantity: 1 }] });
      }
      this.applySections(data.sections);
      this.syncTheme(data);
    } catch (err) {
      console.error('[CartDrawer] addon toggle', err);
      addonEl.classList.toggle('is-checked', isInCart);
    } finally {
      addonEl.classList.remove('is-loading');
    }
  }

  refreshAddonKeys() {
    this.querySelectorAll('[data-cd-addon]').forEach((el) => {
      el.classList.toggle('is-checked', !!el.dataset.addonKey);
    });
  }

  /* ─────────────── Upsell ─────────────── */
  initUpsell() {
    const track = this.recsEl.querySelector('[data-cd-track]');
    if (!track) return;
    const dots = [...this.recsEl.querySelectorAll('[data-cd-dot]')];
    const prev = this.recsEl.querySelector('[data-cd-prev]');
    const next = this.recsEl.querySelector('[data-cd-next]');
    const update = () => {
      const i = Math.round(track.scrollLeft / track.clientWidth);
      dots.forEach((d, idx) => d.classList.toggle('is-active', idx === i));
      if (prev) prev.disabled = i <= 0;
      if (next) next.disabled = i >= dots.length - 1;
    };
    track.addEventListener('scroll', () => { clearTimeout(this._upsellT); this._upsellT = setTimeout(update, 60); });
    update();
  }
  slideUpsell(dir) { const t = this.recsEl.querySelector('[data-cd-track]'); if (t) t.scrollBy({ left: dir * t.clientWidth, behavior: 'smooth' }); }
  slideToUpsell(i) { const t = this.recsEl.querySelector('[data-cd-track]'); if (t) t.scrollTo({ left: i * t.clientWidth, behavior: 'smooth' }); }

  onVariantChange(select) {
    const slide = select.closest('[data-cd-slide]');
    const opt   = select.selectedOptions[0];
    const price = slide?.querySelector('.pb-cd__slide-price');
    const add   = slide?.querySelector('[data-cd-add]');
    if (price && opt?.dataset.price) price.textContent = opt.dataset.price;
    if (add) add.dataset.variantId = select.value;
  }

  async onUpsellAdd(btn) {
    const slide  = btn.closest('[data-cd-slide]');
    const select = slide?.querySelector('[data-cd-variant]');
    const id     = parseInt(select ? select.value : btn.dataset.variantId, 10);
    const label  = btn.textContent;
    btn.setAttribute('disabled', '');
    try {
      await this.addItems([{ id, quantity: 1 }]);
      btn.classList.add('is-added');
      btn.textContent = 'Aggiunto ✓';
      this._recsFor = null;
      setTimeout(() => this.fetchRecommendations(), 700);
    } catch (err) {
      console.error('[CartDrawer] upsell add', err);
      btn.textContent = label;
      btn.removeAttribute('disabled');
    }
  }

  async onCheckout() {
    const note = this.querySelector('[data-cd-note]');
    if (note && note.value !== '') {
      try { await this.post('/cart/update.js', { note: note.value }, false); } catch (e) { /* prosegui */ }
    }
    window.location.href = '/checkout';
  }

  /* ─────────────── Discount ─────────────── */
  async applyDiscount(code) {
    if (!code) return;
    this.setLoading(true);
    const msg = this.querySelector('[data-cd-discount-msg]');
    try {
      const data  = await this.post('/cart/update.js', { discount: code });
      const entry = (data.discount_codes || []).find((d) => d.code.toLowerCase() === code.toLowerCase());
      if (entry?.applicable) {
        this.applySections(data.sections);
        this.syncTheme(data);
      } else if (msg) {
        msg.hidden = false;
        msg.textContent = entry ? 'Codice non applicabile a questi prodotti.' : 'Codice non valido.';
      }
    } catch (err) {
      console.error('[CartDrawer] discount', err);
      if (msg) { msg.hidden = false; msg.textContent = 'Errore, riprova.'; }
    } finally {
      this.setLoading(false);
    }
  }

  async removeDiscount() {
    this.setLoading(true);
    try {
      const data = await this.post('/cart/update.js', { discount: '' });
      this.applySections(data.sections);
      this.syncTheme(data);
    } catch (err) {
      console.error('[CartDrawer] discount remove', err);
    } finally {
      this.setLoading(false);
    }
  }

  /* ─────────────── Section Rendering → DOM (swap solo se cambia) ─────────────── */
  applySections(sections) {
    if (!sections || !sections[this.sectionId]) return;
    const doc = new DOMParser().parseFromString(sections[this.sectionId], 'text/html');
    const src = doc.querySelector(PB_TAG) || doc;

    // Items: sostituisci solo se diverso e anima solo le righe nuove
    const itemsCur = this.querySelector('[data-cd-items]');
    const itemsInc = src.querySelector('[data-cd-items]');
    if (itemsCur) {
      const html = itemsInc ? itemsInc.innerHTML : '';
      if (itemsCur.innerHTML !== html) {
        const prevKeys = new Set([...itemsCur.querySelectorAll('[data-cd-item]')].map((li) => li.dataset.key));
        itemsCur.innerHTML = html;
        itemsCur.querySelectorAll('[data-cd-item]').forEach((li) => {
          if (!prevKeys.has(li.dataset.key)) li.classList.add('is-new');
        });
      }
    }

    const footCur = this.querySelector('[data-cd-footer]');
    const footInc = src.querySelector('[data-cd-footer]');
    if (footCur) {
      const html = footInc ? footInc.innerHTML : '';
      if (footCur.innerHTML !== html) footCur.innerHTML = html;
    }

    // Barra traguardi con fill animato
    const barCur = this.querySelector('[data-cd-bar]');
    const barInc = src.querySelector('[data-cd-bar]');
    if (barCur) {
      const html = barInc ? barInc.innerHTML : '';
      if (barCur.innerHTML !== html) {
        const prevWidth = barCur.querySelector('.pb-cd__bar-fill')?.style.width ?? null;
        barCur.innerHTML = html;
        const newFill = barCur.querySelector('.pb-cd__bar-fill');
        if (newFill && prevWidth != null) {
          const target = newFill.style.width;
          newFill.style.transition = 'none';
          newFill.style.width = prevWidth;
          void newFill.offsetWidth;
          requestAnimationFrame(() => { newFill.style.transition = ''; newFill.style.width = target; });
        }
      }
    }

    const count = src.getAttribute('data-cd-count') || '0';
    this.dataset.cdCount = count;
    const label = this.querySelector('[data-cd-count-label]');
    if (label) label.textContent = count;
    this.classList.toggle('pb-cd--empty', parseInt(count, 10) === 0);
    this.updateCartCount(parseInt(count, 10));
    this.refreshAddonKeys();
  }

  /* ─────────────── Recommendations (no refetch se stesso prodotto) ─────────────── */
  async fetchRecommendations() {
    if (!this.recsEl) return;
    const firstItem = this.querySelector('[data-cd-item]:not([data-free])') || this.querySelector('[data-cd-item]');
    const productId = this.lastAddedProductId || firstItem?.dataset.productId;
    if (!productId) { this.recsEl.innerHTML = ''; this._recsFor = null; return; }
    if (this._recsFor === productId && this.recsEl.children.length) return;
    this._recsFor = productId;

    const intent = this.dataset.upsellIntent || 'complementary';
    const limit  = this.dataset.upsellLimit || 4;
    const url = `${this.urls.recs}?section_id=cart-recommendations&product_id=${productId}&intent=${intent}&limit=${limit}`;
    try {
      const res   = await fetch(url);
      const doc   = new DOMParser().parseFromString(await res.text(), 'text/html');
      const block = doc.querySelector('[data-cd-upsell]');
      if (!block) { this.recsEl.innerHTML = ''; return; }
      const heading = this.recsEl.dataset.heading;
      const titleEl = block.querySelector('.pb-cd__upsell-title');
      if (heading && titleEl) titleEl.textContent = heading;
      this.recsEl.innerHTML = block.outerHTML;
      this.initUpsell();
    } catch (err) {
      console.error('[CartDrawer] recommendations', err);
      this.recsEl.innerHTML = '';
      this._recsFor = null;
    }
  }

  refresh() { this._recsFor = null; this.refreshFromServer(); this.fetchRecommendations(); }
}

if (!customElements.get(PB_TAG)) customElements.define(PB_TAG, CartDrawer);
const drawer = () => document.querySelector(PB_TAG);

/* ════════════════ INTEGRAZIONE TEMA ════════════════ */

// Click icona carrello → drawer PB (blocca il nativo)
document.addEventListener('click', (e) => {
  const trigger = e.target.closest(CART_TRIGGER_SELECTORS);
  if (!trigger) return;
  const d = drawer();
  if (!d) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  d.open();
}, true);

// Intercettazione form SOLO se intercept=true (default OFF su Pitch)
document.addEventListener('submit', (e) => {
  const d = drawer();
  if (!d || d.dataset.intercept !== 'true') return;
  const form = e.target.closest('form[action*="/cart/add"]');
  if (!form || form.hasAttribute('data-no-drawer')) return;
  e.preventDefault();
  e.stopImmediatePropagation();
  d.addFromForm(form, e.submitter);
}, true);

// Pitch/Horizon nuovo: @shopify/events → cartLinesUpdate (lo stesso di cart-drawer-component)
if (StandardEvents?.cartLinesUpdate) {
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    const d = drawer();
    if (!d) return;
    const isAdd = e.action === 'add' || e.detail?.action === 'add';
    const after = (detail) => {
      if (detail?.didError) return;
      const pid = detail?.productId || detail?.data?.productId || detail?.resource?.product_id || detail?.resource?.items?.[0]?.product_id;
      if (pid) d.lastAddedProductId = String(pid);
      if (isAdd) d.openAfterRefresh();
      else d.scheduleRefresh();
    };
    if (e.promise?.then) e.promise.then((r) => after(r?.detail ?? r)).catch(() => {});
    else after(e.detail);
  });
}

// Horizon legacy: cart:update (CartAddEvent / CartUpdateEvent)
document.addEventListener('cart:update', (e) => {
  const d = drawer();
  if (!d || e.detail?.data?.source === SRC) return;
  const pid = e.detail?.data?.productId;
  if (pid) d.lastAddedProductId = String(pid);
  if (pid || e.detail?.data?.variantId) d.openAfterRefresh();
  else d.scheduleRefresh();
});

// Bogos (free gift): quando finisce di processare il carrello, riallinea il drawer
['fg-cart:end-process', 'fg-cart:auto-updated', 'fg-gifts:added', 'bogos:gifts-end'].forEach((ev) => {
  document.addEventListener(ev, () => {
    const d = drawer();
    if (d?.isOpen) d.scheduleRefresh(250);
  });
});

window.openCartDrawer = () => drawer()?.open();